<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <base-widget
    icon="currency-dollar"
    :loading="isLoading"
    :title="$t('general.invoices')"
  >
    <oxd-bar-chart
      :grid="false"
      :data="dataset"
      :y-axsis="false"
      :aspect-ratio="false"
      wrapper-classes="emp-attendance-chart"
    ></oxd-bar-chart>
    <oxd-divider />
    <div class="orangehrm-attendance-card-summary">
      <div class="orangehrm-attendance-card-summary-week">
        <oxd-text tag="p"> Total Owed: {{ totalOwed }} </oxd-text>
        <oxd-text tag="p"> Total Paid: {{ totalPaid }} </oxd-text>
        <oxd-text tag="p">
          Outstanding: {{ (totalOwed - totalPaid).toFixed(2) }}
        </oxd-text>
      </div>
    </div>
    <oxd-pie-chart
      :data="piedataset"
      :aspect-ratio="false"
      :custom-legend="true"
      :custom-tooltip="true"
      wrapper-classes="emp-attendance-chart"
    ></oxd-pie-chart>
  </base-widget>
</template>

<script>
import {
  isToday,
  freshDate,
  parseDate,
  formatDate,
} from '@/core/util/helper/datefns';
import {navigate} from '@/core/util/helper/navigation';
import useLocale from '@/core/util/composable/useLocale';
import {APIService} from '@/core/util/services/api.service';
import BaseWidget from '@/orangehrmDashboardPlugin/components/BaseWidget.vue';
import {OxdBarChart, OxdPieChart, OxdIcon, CHART_COLORS} from '@ohrm/oxd';

export default {
  name: 'InvoiceWidget',

  components: {
    'base-widget': BaseWidget,
    'oxd-bar-chart': OxdBarChart,
    'oxd-pie-chart': OxdPieChart,
  },

  setup() {
    const {locale} = useLocale();
    const http = new APIService(
      window.appGlobal.baseUrl,
      'api/v2/pim/invoicepayments/0/0',
    );
    const httpinv = new APIService(
      window.appGlobal.baseUrl,
      'api/v2/pim/invoices/cust/0/month/1',
    );
    const httpcust = new APIService(
      window.appGlobal.baseUrl,
      'api/v2/time/customers',
    );

    return {
      http,
      httpinv,
      httpcust,
      locale,
    };
  },

  data() {
    return {
      dataset: [],
      piedataset: [],
      customers: [],
      isLoading: false,
      totalPaid: 0,
      totalOwed: 0,
    };
  },

  computed: {},

  async beforeMount() {
    await this.fetchCustomers();
    this.fetchWidgetData();
    this.fetchInvoices();
  },
  methods: {
    getNameById(id) {
      const customer = this.customers.find(
        (customer) => customer.id === Number(id),
      );
      return customer ? customer.name : 'Customer not found';
    },
    fetchWidgetData() {
      this.isLoading = true;
      this.http
        .getAll({})
        .then((response) => {
          const {data, meta} = response.data;

          const colors = [
            CHART_COLORS.COLOR_HEAT_WAVE,
            CHART_COLORS.COLOR_CHROME_YELLOW,
            CHART_COLORS.COLOR_YELLOW_GREEN,
            CHART_COLORS.COLOR_MOUNTAIN_MEADOW,
            CHART_COLORS.COLOR_PACIFIC_BLUE,
            CHART_COLORS.COLOR_BLEU_DE_FRANCE,
            CHART_COLORS.COLOR_MAJORELLE_BLUE,
            CHART_COLORS.COLOR_MEDIUM_ORCHID,
            CHART_COLORS.COLOR_FANDANGO_PINK,
          ];

          // Group the amounts by customerId
          const amountsByCustomer = data.reduce((acc, item) => {
            const customerId = this.getNameById(item.customerId);
            acc[customerId] = (acc[customerId] || 0) + item.amount;
            return acc;
          }, {});

          // Prepare data for the pie chart
          const pielabels = Object.keys(amountsByCustomer); // Customer IDs
          const amounts = Object.values(amountsByCustomer); // Corresponding amounts

          // Update the dataset
          this.piedataset = pielabels.map((cust, index) => ({
            label: cust,
            value: amounts[index],
            color: colors[index],
          }));

          // Group data by month
          const monthlyData = data.reduce((acc, invoice) => {
            const month = new Date(invoice.invoiceDate.date)
              .toISOString()
              .slice(0, 7); // Get year-month
            acc[month] = (acc[month] || 0) + invoice.amount;
            return acc;
          }, {});
          // Prepare data for the chart
          const labels = Object.keys(monthlyData);
          const chartData = Object.values(monthlyData);
          const {total, totalAmount} = meta;
          this.totalPaid = totalAmount.toFixed(2);
          // Update the dataset
          this.dataset = labels.map((month, index) => ({
            label: month,
            value: chartData[index],
            color: colors[index], //CHART_COLORS.COLOR_HEAT_WAVE, // Use a predefined color or customize as needed
          }));
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    fetchCustomers() {
      this.isLoading = true;
      this.httpcust
        .getAll({})
        .then((response) => {
          const {data, meta} = response.data;
          this.customers = data;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    fetchInvoices() {
      this.isLoading = true;
      this.httpinv
        .getAll({})
        .then((response) => {
          const {data, meta} = response.data;
          const {total, totalCost} = meta;
          this.totalOwed = totalCost.toFixed(2);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style src="./employee-attendance-widget.scss" lang="scss" scoped></style>
